import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

//ENUM: "Beef", "Fish", "Vegetarian", "Children's Meal"
const entreeMapper = {
    "Beef": { displayName: "Beef", description: "Prime rib steak with galbi sauce, potato purée, bok choy" },
    "Fish": { displayName: "Fish", description: "Miso-glazed Chilean sea bass, black rice, bok choy" },
    "Vegetarian": { displayName: "Vegetarian", description: "Champagne gnocchi, roasted vegetables, thyme cream sauce" },
    "Children's Meal": { displayName: "Children's Meal", description: "We will contact parents in advance to coordinate" }
};


function RSVPForm({ selectedRSVP, setSelectedRSVP, allResponses, error, loading }) {
    const original_data = selectedRSVP
    const [searchParams] = useSearchParams();
    const responseId = searchParams.get("response") || "";
    const navigate = useNavigate();

    function generateFormData(rsvp) {
        return rsvp ? ({
            id: rsvp._id,
            email: rsvp.email || "",
            phone: rsvp.phone || "",
            comments: rsvp.comments || "",
            guests: rsvp.guests.map((g) => ({
                ...g,
                age_group: g.age_group,
                attend_wedding: g.attend_wedding,
                attend_welcome_party: g.attend_welcome_party,
                wedding_entree: g.wedding_entree,
                dietary_restrictions: g.dietary_restrictions || "",
            })),
        }) : null;
    }

    const [formState, setFormState] = useState(generateFormData(selectedRSVP));
    const [isCommentsFocused, setIsCommentsFocused] = useState(false);
    const [submitError, setSubmitError] = useState(false)
    const [waitingForResponse, setWaitingForResponse] = useState(false)

    useEffect(() => {
        if (!selectedRSVP) {
            let foundRSVP = allResponses.find(response => response["_id"] === responseId)
            setFormState(generateFormData(foundRSVP))
            setSelectedRSVP(foundRSVP)
        }
    }, [allResponses]);

    function isFormDirty() {
        // Check email, phone, comments
        if (formState.email.trim() !== "") return true;
        if (formState.phone.trim() !== "") return true;
        if (formState.comments.trim() !== "") return true;

        // Check guests
        // Compare each guest field that matters
        const guestsChanged = formState.guests.some((guest, i) => {
            const origGuest = original_data.guests[i];
            if (!origGuest) return true; // New guest added somehow
            return (
                guest.attend_wedding !== origGuest.attend_wedding ||
                guest.attend_welcome_party !== origGuest.attend_welcome_party ||
                guest.wedding_entree !== origGuest.wedding_entree ||
                (guest.dietary_restrictions || "") !== (origGuest.dietary_restrictions || "")
            );
        });
        if (guestsChanged) return true;

        return false; // Nothing has changed
    }

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isFormDirty(formState, original_data)) {
                e.preventDefault();
                e.returnValue = ""; // This triggers the browser warning
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [formState]);

    const handleChange = (field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
    };

    const handleGuestChange = (id, field, value) => {
        setFormState((prev) => {
            const guests = [...prev.guests];
            const index = guests.findIndex(guest => guest.id === id)
            guests[index] = { ...guests[index], [field]: value };
            return { ...prev, guests };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!waitingForResponse) {
            setWaitingForResponse(true)
            try {
                const response = await fetch(
                    "https://wedding-website-backend-nu.vercel.app/api/responses",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(formState),
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const res = await response.json();
                if (res.result.modifiedCount === 1) {
                    navigate(`/rsvp/finished?success=true`)
                } else {
                    setSubmitError(true)
                    navigate(`/rsvp/finished?success=false`)
                }
            }
            catch (error) {
                setSubmitError(true)
                navigate(`/rsvp/finished?success=false`)
            }
            finally {
                setWaitingForResponse(false)
                setSelectedRSVP(null)
            }
        }
    };

    return !loading && (
        <div className="space-y-4 flex flex-col items-center">
            {error ? <p className="text-red-400">Website Error! Failed to fetch guests information. Please reach out to Thuyan to get this fixed.</p> :
                <>
                    {
                        selectedRSVP ? (
                            <>
                                <form onSubmit={handleSubmit} className="space-y-6 text-base md:w-96">
                                    <p className="text-gray-900 text-lg">Contact Information:</p>
                                    <div className="space-y-3">
                                        {/* Email */}
                                        <div>
                                            <label className="">Email<span className="text-red-300">*</span></label>
                                            <input
                                                type="email"
                                                value={formState.email}
                                                onChange={(e) => handleChange("email", e.target.value)}
                                                className="border border-rose text-center focus:outline-none p-2 w-full"
                                                required
                                                onInvalid={(e) => e.target.setCustomValidity("Please enter a valid email address")}
                                                onInput={(e) => e.target.setCustomValidity("")}
                                            />
                                            <p className="text-xs">
                                                You will get an email confirmation after you RSVP.
                                            </p>
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="">Phone<span className="text-red-300">*</span></label>
                                            <input
                                                type="text"
                                                value={formState.phone}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/[^0-9+()\- ]/g, "");
                                                    handleChange("phone", value);
                                                }}
                                                className="border border-rose text-center focus:outline-none p-2 w-full"
                                                required
                                                onInvalid={(e) => e.target.setCustomValidity("Please enter a phone number")}
                                                onInput={(e) => e.target.setCustomValidity("")}
                                            />
                                            <p className="text-xs">
                                                A contact number in case we need to reach you.
                                            </p>
                                        </div>
                                    </div>
                                    {/* Guests */}
                                    <p className="text-gray-900 text-lg">RSVP for each member of your group for both the Welcome Party Friday evening and the wedding celebration on Saturday.</p>
                                    <div className="space-y-8">
                                        {formState.guests.map((guest) => {
                                            return (
                                                <div key={guest.id} className="p-4 border border-rose rounded space-y-4">
                                                    <h3 className="subheading text-lg">{guest.name.toUpperCase()}</h3>
                                                    {/* Welcome Party */}
                                                    <div>
                                                        <span className="block mb-1">Attending Welcome Party on Friday?<span className="text-red-300">*</span></span>
                                                        <label className="mr-4">
                                                            <input
                                                                type="radio"
                                                                required
                                                                className="accent-rose cursor-pointer"
                                                                name={`welcome-${guest.id}`}
                                                                checked={guest.attend_welcome_party === true}
                                                                onChange={() => handleGuestChange(guest.id, "attend_welcome_party", true)}
                                                            /> Yes
                                                        </label>
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                required
                                                                className="accent-rose cursor-pointer"
                                                                name={`welcome-${guest.id}`}
                                                                checked={guest.attend_welcome_party === false}
                                                                onChange={() => handleGuestChange(guest.id, "attend_welcome_party", false)}
                                                            /> No
                                                        </label>
                                                    </div>

                                                    {/* Wedding */}
                                                    <div>
                                                        <span className="block mb-1">Attending Wedding on Saturday?<span className="text-red-300">*</span></span>
                                                        <label className="mr-4">
                                                            <input
                                                                className="accent-rose cursor-pointer"
                                                                type="radio"
                                                                required
                                                                name={`wedding-${guest.id}`}
                                                                checked={guest.attend_wedding === true}
                                                                onChange={() => handleGuestChange(guest.id, "attend_wedding", true)}
                                                            /> Yes
                                                        </label>
                                                        <label>
                                                            <input
                                                                className="accent-rose cursor-pointer"
                                                                type="radio"
                                                                required
                                                                name={`wedding-${guest.id}`}
                                                                checked={guest.attend_wedding === false}
                                                                onChange={() => handleGuestChange(guest.id, "attend_wedding", false)}
                                                            /> No
                                                        </label>
                                                    </div>

                                                    {/* Entree Choice */}
                                                    {
                                                        formState.guests.find(g => g.id === guest.id).attend_wedding &&
                                                        guest.age_group !== "Toddler" && (
                                                            <div>
                                                                <span className="block mb-1">
                                                                    Entrée Choice<span className="text-red-300">*</span>
                                                                </span>
                                                                <div className="space-y-3">
                                                                    {(guest.age_group === "Adult"
                                                                        ? ["Beef", "Fish", "Vegetarian"]
                                                                        : ["Children's Meal"]
                                                                    ).map((option) => (
                                                                        <div key={option}>
                                                                            <label key={option} className="block">
                                                                                <input
                                                                                    className="accent-rose cursor-pointer mr-2"
                                                                                    type="radio"
                                                                                    required
                                                                                    name={`entree-${guest.id}`}
                                                                                    checked={guest.wedding_entree === option}
                                                                                    onChange={() =>
                                                                                        handleGuestChange(guest.id, "wedding_entree", option)
                                                                                    }
                                                                                />
                                                                                {entreeMapper[option].displayName}
                                                                            </label>
                                                                            <p className="text-xs">
                                                                                {entreeMapper[option].description}
                                                                            </p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )
                                                    }

                                                    {/* Dietary Restrictions */}
                                                    <div>
                                                        <label className="block font-semibold">Dietary Restrictions/Allergies</label>
                                                        <textarea
                                                            value={guest.dietary_restrictions}
                                                            placeholder="Please list all dietary restrictions and allergies for this guest."
                                                            onChange={(e) => handleGuestChange(guest.id, "dietary_restrictions", e.target.value)}
                                                            className="placeholder-gray-300 border border-rose text-center focus:outline-none p-2 w-full"
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>

                                    <p className="text-gray-900 text-lg">General Comments:</p>
                                    <div>
                                        <textarea
                                            value={formState.comments}
                                            onChange={(e) => handleChange("comments", e.target.value)}
                                            className="placeholder-gray-300 border border-rose text-center focus:outline-none p-2 w-full"
                                            placeholder={isCommentsFocused && !formState.comments ? "" : "Leave a note for the couple or let us know about any name corrections."}
                                            onFocus={() => setIsCommentsFocused(true)}
                                            onBlur={() => setTimeout(() => setIsCommentsFocused(false), 100)}
                                        />
                                    </div>

                                    <button type="submit" className={`${waitingForResponse ? "bg-rose text-white cursor-default" : "bg-blush text-customGray"} hover:bg-rose hover:text-white border-2 px-4 py-2 rounded border-rose`}>
                                        Submit RSVP
                                    </button>
                                    {submitError &&
                                        <div className="text-xs text-red-400">Error! Something went wrong while saving your response. Please try again. If the issue persists, please contact the couple for help.</div>
                                    }

                                </form>
                            </>
                        ) :
                            <p>RSVP Response Not Found</p>
                    }

                    <div className="h-2"></div>
                    <p
                        className="pb-8 back-to-groups text-base text-bronze hover:text-gold hover:underline hover:cursor-pointer"
                        onClick={(e) => {
                            if (selectedRSVP) {
                                if (isFormDirty()) {
                                    if (!window.confirm("You haven't submitted the RSVP yet. Are you sure you want to leave?")) {
                                        return; // stop navigation
                                    }
                                }
                                setSelectedRSVP(null)
                            }
                            navigate(`/rsvp`);
                        }}>
                        Go Back
                    </p>
                </>
            }
        </div>
    );
}

export default RSVPForm;